import { useState } from 'react';
import css from './FAQPage.module.css';
import classNames from 'classnames';
import Icons from '../../components/Icons/Icons';

const FAQSection = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const handleAccordionClick = index => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  const faqs = [
    {
      question: 'What is SwimMarketplace and how does it work?',
      answer: (
        <p>
          SwimMarketplace is a peer to peer marketplace for buying and selling swim gear (swimsuits,
          tech suits, goggles, caps, training gear, and more). Sellers list items with photos and
          details, buyers shop and purchase on the platform, and sellers ship the items. We provide
          secure checkout and support if an item is not received or is not as described.
        </p>
      ),
    },
    {
      question: 'Does it cost anything to use SwimMarketplace?',
      answer: (
        <p>
          Creating an account is free, and it&apos;s free to list items. SwimMarketplace charges
          sellers fees only when an item sells. Buyers do not pay a marketplace fee (buyers pay the
          item price plus shipping and any applicable taxes shown at checkout).
        </p>
      ),
    },
    {
      question: 'What are the seller fees?',
      answer: (
        <>
          <p>When an item sells, sellers pay:</p>
          <p>
            • 15% seller transaction fee (based on the item sale price), plus
            <br />
            • 1% payment processing fee (based on the item sale price).
            <br />
            Both fees are deducted from the seller&apos;s payout.
          </p>
        </>
      ),
    },
    {
      question: 'Do buyers pay any SwimMarketplace fees?',
      answer: <p>No. Buyers do not pay a marketplace fee to SwimMarketplace.</p>,
    },
    {
      question: 'Can you give an example of fees?',
      answer: (
        <>
          <p>If an item sells for $100:</p>
          <p>
            • 15% seller fee = $15
            <br />
            • 1% processing fee = $1
            <br />• Seller payout = $84 (subject to any refunds/adjustments)
          </p>
        </>
      ),
    },
    {
      question: 'Are items new or used? Can I trust the quality of used gear?',
      answer: (
        <p>
          SwimMarketplace includes both new and used items, and each listing should clearly state
          the condition (for example: New with tags, Like New, Gently Used, etc.). We encourage
          sellers to post clear photos and honest descriptions. Before buying, review listing photos
          closely, read the full description, and check the seller&apos;s profile and reviews when
          available.
        </p>
      ),
    },
    {
      question: 'How do I pay for an item, and is payment secure?',
      answer: (
        <p>
          Checkout happens directly on SwimMarketplace through secure payment processing. Always
          keep payments on-platform so you&apos;re eligible for support if something goes wrong.
        </p>
      ),
    },
    {
      question: 'What payment methods do you accept?',
      answer: (
        <p>
          SwimMarketplace supports major credit/debit card payments through our payment provider.
          (Available methods may vary slightly by location.)
        </p>
      ),
    },
    {
      question: 'Someone asked me to pay off-platform (Venmo/Zelle/PayPal). Should I?',
      answer: (
        <p>
          No. Off-platform payments are not allowed and can remove the protections and support
          SwimMarketplace can provide. If someone asks to pay off-platform, don&apos;t
          proceed&mdash;report it to Support.
        </p>
      ),
    },
    {
      question: 'How does shipping work? Who pays for shipping?',
      answer: (
        <p>
          Shipping costs (if any) are shown during checkout. In most cases, the buyer pays shipping,
          unless the seller offers free shipping or the listing states otherwise. After purchase,
          the seller is responsible for packaging and shipping the item and providing tracking when
          available.
        </p>
      ),
    },
    {
      question: 'How do I choose my shipping method?',
      answer: (
        <p>
          Shipping options depend on what the seller offers for that listing. If multiple shipping
          speeds/options are available, you&apos;ll see them at checkout. Otherwise, the
          listing&apos;s shipping method applies.
        </p>
      ),
    },
    {
      question: 'What if my item never arrives (Not Received)?',
      answer: (
        <p>
          If an item is not delivered, you can open a request within 10 calendar days of the
          estimated delivery date (or within 10 days of the actual delivery date if the carrier
          marks it delivered but you did not receive it). We&apos;ll review tracking and details and
          help move the situation toward a resolution.
        </p>
      ),
    },
    {
      question: "What if my item isn't as described (Not As Described)?",
      answer: (
        <p>
          If an item is materially different than the listing described, open a request within 10
          calendar days of the delivery date. Include clear photos and a description of what&apos;s
          different.
        </p>
      ),
    },
    {
      question: 'What counts as "Not As Described"?',
      answer: (
        <>
          <p>
            &quot;Not As Described&quot; generally means a material difference from the listing,
            such as:
          </p>
          <p>
            • wrong item, brand, model, or size
            <br />
            • counterfeit item
            <br />
            • major undisclosed damage/defects not shown or stated
            <br />• missing parts/components that were included or reasonably implied
          </p>
          <p>Not usually considered &quot;Not As Described&quot;:</p>
          <p>
            • fit issues when the listed size/details were accurate
            <br />
            • minor wear consistent with the condition described
            <br />• buyer&apos;s remorse or preference changes
          </p>
        </>
      ),
    },
    {
      question: 'How long does the seller have to respond to a request?',
      answer: <p>Sellers have 3 business days to respond once a request is opened.</p>,
    },
    {
      question: 'When does SwimMarketplace step in?',
      answer: (
        <p>
          If the issue isn&apos;t resolved after 3 business days, either party can ask
          SwimMarketplace to step in. We&apos;ll review evidence such as listing details, photos,
          messages, and tracking and determine an outcome.
        </p>
      ),
    },
    {
      question: 'Can I return an item?',
      answer: (
        <p>
          Returns are available only for issues like Not As Described (materially different from the
          listing) or Not Received, within the timelines above. SwimMarketplace does not offer
          returns for buyer&apos;s remorse or fit issues when the listing was accurate.
        </p>
      ),
    },
    {
      question: 'Who pays return shipping if the item is Not As Described?',
      answer: (
        <p>
          If a return is approved because the item is Not As Described, the seller pays return
          shipping. Depending on the situation, SwimMarketplace may require the seller to provide a
          prepaid label, or we may provide a label and deduct the cost from the seller&apos;s payout
          (or require reimbursement as part of the resolution).
        </p>
      ),
    },
    {
      question: 'How long do I have to ship the item back after a return is approved?',
      answer: (
        <p>
          If a return is approved, the buyer must ship the return within 7 calendar days after
          receiving return instructions/label (unless SwimMarketplace specifies otherwise). Missing
          the deadline may affect eligibility for a refund.
        </p>
      ),
    },
    {
      question: 'What happens if no one responds in a request?',
      answer: <p>Requests may automatically close after 14 business days with no activity.</p>,
    },
    {
      question: 'How do I sell an item on SwimMarketplace?',
      answer: (
        <p>
          Create an account (free), click &quot;Sell&quot; or &quot;List Your Gear,&quot; then add
          photos, item details (brand, model, size, condition), and a price. Once published, your
          listing is visible to buyers. When it sells, ship it promptly and provide tracking when
          available.
        </p>
      ),
    },
    {
      question: 'What should I do to avoid "Not As Described" issues?',
      answer: (
        <>
          <p>
            • Use clear photos (front/back + labels + seams + any flaws)
            <br />
            • Be accurate about brand/model/size
            <br />
            • Disclose defects and wear honestly
            <br />• For tech suits, include notes on wear and condition (for example: approximate
            number of wears, meet-only vs training use, seam/stretch issues)
          </p>
        </>
      ),
    },
    {
      question: 'What if a buyer files a credit card chargeback?',
      answer: (
        <>
          <p>If a buyer files a payment dispute through their bank:</p>
          <p>
            • Sellers usually have 5 calendar days to respond once notified.
            <br />• Disputes can take 90 days or more to resolve, and funds may be held during that
            time.
          </p>
          <p>
            Sellers should respond quickly with documentation (listing details, messages, shipping
            proof, delivery confirmation, photos).
          </p>
        </>
      ),
    },
    {
      question: 'How do I contact SwimMarketplace Support?',
      answer: (
        <p>
          Email{' '}
          <a href="mailto:info@swimmarketplace.com" className={css.supportEmailLink}>
            info@swimmarketplace.com
          </a>
          .
        </p>
      ),
    },
    {
      question: 'When will you respond?',
      answer: (
        <p>
          We reply within 24 hours (Mon&ndash;Fri). Weekend messages are answered as soon as
          possible.
        </p>
      ),
    },
    {
      question: 'What should I include so you can help fast?',
      answer: (
        <p>
          Include your account email, the listing link, the order link/number, what happened and
          when, photos (for Not As Described), and tracking details (for Not Received).
        </p>
      ),
    },
  ];

  return (
    <div className={css.faqContentWrapper}>
      <div className={css.fixedWidthContainer}>
        <div className={css.faqContent}>
          <div className={css.faqSection}>
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={classNames(css.faqCard, {
                  [css.activeFAQ]: activeIndex === index,
                })}
                onClick={() => handleAccordionClick(index)}
              >
                <div className={css.faqBody}>
                  <span className={css.faqHeading}>{faq?.question} </span>{' '}
                  {activeIndex === index && <div className={css.faqDescription}>{faq?.answer}</div>}
                </div>
                <span className={css.faqIcon}>
                  {activeIndex === index ? (
                    <span>
                      <Icons name="faqOpenIcon" />
                    </span>
                  ) : (
                    <span>
                      <Icons name="faqCloseIcon" />
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQSection;
